import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import isFunction from 'lodash/isFunction';
import isObject from 'lodash/isObject';

import { hook } from '@g4/automation-hooks';
import { Box, VisuallyHidden, Radio } from '../../Boneyard';

export const LAYOUT_HORIZONTAL = 'horizontal';
export const LAYOUT_VERTICAL = 'vertical';

export const propTypes = {
  /**
   * Describes the set of controls
   *
   * For example: "Filter by price range"
   */
  a11yDescription: PropTypes.string.isRequired,
  /** Input field `name` attribute value */
  name: PropTypes.string.isRequired,
  /** The value */
  value: PropTypes.string,
  /** Visual layout */
  layout: PropTypes.oneOf([LAYOUT_HORIZONTAL, LAYOUT_VERTICAL]),
  /** The icon to display when the input is checked */
  iconChecked: PropTypes.element,
  /** The icon to display when the input is unchecked */
  iconUnchecked: PropTypes.element,
  /** Disabled status */
  disabled: PropTypes.bool,
  /** Collection of options */
  options: PropTypes.oneOfType([
    PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.node,
        value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      })
    ),
    PropTypes.arrayOf(
      PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    ),
  ]).isRequired,
  /** onchange callback handler */
  onChange: PropTypes.func.isRequired,
  /**
   * Associates a specific action onto an element for the benefit of automated clients consuming it
   *
   * Also used as the prefix for radio field automation hooks
   */
  errorId: PropTypes.string,
  automationHook: PropTypes.string,
  className: PropTypes.string,
  children: PropTypes.func,
  'aria-describedby': PropTypes.string,
  hasError: PropTypes.bool,
};

export const defaultProps = {
  layout: LAYOUT_HORIZONTAL,
  hasError: false,
};

/** @component */
/** @visibleName Radio Group */
const RadioGroup = styled(({ options, ...props }) => {
  const fields = options.map(option => {
    const value = isObject(option) ? option.value : option;
    const label = isObject(option) ? option.label : option.toString();

    return React.createElement(Radio, {
      name: props.name,
      key: value,
      checked: props.value === value,
      disabled: props.disabled || Boolean(option?.disabled),
      iconChecked: props.iconChecked,
      iconUnchecked: props.iconUnchecked,
      automationHook: props.automationHook,
      onChange: props.onChange,
      value,
      label,
    });
  });

  return (
    <fieldset
      aria-describedby={props.hasError ? props.errorId : undefined}
      aria-labelledby={`${props.name}_label`}
      className={props.className}
      {...hook(props.automationHook)}
      aria-invalid={props.hasError}
    >
      <legend>
        <VisuallyHidden
          id={`${props.name}_label`}
          children={props.a11yDescription}
        />
      </legend>
      <Box
        d="flex"
        direction={props.layout === LAYOUT_VERTICAL ? 'column' : 'row'}
      >
        {isFunction(props.children)
          ? props.children(fields, { options })
          : fields}
      </Box>
    </fieldset>
  );
})`
  ${props =>
    props.layout === LAYOUT_HORIZONTAL &&
    `
    ${Radio} + ${Radio} {
      margin-left: ${props.theme.spacing(2)};
    }
  `}
  ${props =>
    props.layout === LAYOUT_VERTICAL &&
    `
    ${Radio} + ${Radio} {
      margin-top: ${props.theme.spacing(1)};
    }
  `}
`;

RadioGroup.propTypes = propTypes;
RadioGroup.defaultProps = defaultProps;

export default RadioGroup;
